/**
 * @file        worker-config.js - Use the scheduler database to supply configuration for the worker.
 *
 *              There are two configuration tables in the db, and they are used for configuring
 *              different settings at different precedence levels.
 *
 *              . DistributiveWorkerConfigKVP: contains key-value-pairs which are applied to a clone of
 *                the config argument (normally dcpConfig.worker) of the DistributiveWorker constructor.
 *                These are fetched and merged with worker.config as the worker is instanciated. The
 *                ::start() method will wait for this to complete before fetching its first task.
 *
 *              . dcpWorkerConfig: contains at most one row per worker, and is used to configure options
 *                specific to the dcp-worker program. Each column in the row which is not null is
 *                applied to the running program's config early on in the command-line parsing phases.
 *
 *              Each configuiration row has an optional workerOpaqueId; when this is null it means that
 *              the row applies to every worker (belonging to that owner). Rows are ordered by
 *              workerOpaqueId, so that worker-specific entries override generic entries.
 *
 * @author      Ryan Saweczko, ryansaweczko@distributive.network
 * @date        Nov 2024
 * @author      Wes Garland, wes@distributive..network
 * @date        Jun 2025
 */
'use strict';

const jnu = require('dcp/dcp-client/jnu');
const debugging = require('dcp/debugging').scope('worker');
const { assert } = require('dcp/common/dcp-assert');
const { jnuConnect } = require('.');

/** See proxy generation in althea's register-worker method for details */
function wcWorkerIdOrNullPrototype(worker)
{
  return { workerOpaqueId: { in: [ null, worker.id ] } };
}

/**
 * Fetch config from scheduler db, update running worker accordingly
 */
exports.applyDbConfig = async function applyDbConfig(worker)
{
  if (worker.config.unmanaged)
    return;

  const db = await jnuConnect();
  const kvp = await db.select({
    table: 'w$workerConfigValue',
    prototype: wcWorkerIdOrNullPrototype(worker),
    order: [ { keyPath: 'asc' }, { workerOpaqueId: 'desc' } ],
  });

  for (let row = await kvp.getFirst(); row; row = await kvp.getNext())
  {
    const path = row.keyPath.split('.');
    const leaf = path.pop();
    let node;
    // traverse the key path until the leaf node, then assign value
    for (node = workerOptions; path.length; node=node[path.shift()]) {  /* noop - traversing the object */ } //eslint-disable-line
    node[leaf] = row.value;
  }
}
